/**
 * CRS catalog module per RFC-112.
 * 
 * Provides unified access to CRS definitions via bundled catalog,
 * IDB cache, and HTTP fallback.
 */

import { get, set } from 'idb-keyval';
import bundledCatalog from './catalog.json';

export interface CrsDef {
  epsgCode: string;          // "EPSG:4978" | "LOCAL"
  name: string;
  kind: 'geographic' | 'projected' | 'geocentric' | 'compound' | 'vertical' | 'engineering' | 'other';
  proj4: string;
  wkt: string;
  bbox: [number, number, number, number] | null;
  units: string;
}

export class UnknownCrsError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
    public readonly cause?: unknown
  ) {
    super(message || `Unknown CRS: ${code}`);
    this.name = 'UnknownCrsError';
  }
}

const BUNDLED_ENTRIES = bundledCatalog.entries as unknown as Record<string, CrsDef>;

// Get base URL for converter API from environment with fallback
const getConverterApiBaseUrl = (): string => {
  return import.meta.env.VITE_CONVERTER_API_BASE_URL || 'http://localhost:3011';
};

/**
 * Normalize CRS code to canonical form.
 * 
 * Handles various input formats:
 * - "EPSG:4978", "epsg:4978" -> "EPSG:4978"
 * - "4978" -> "EPSG:4978"  
 * - "urn:ogc:def:crs:EPSG::4978" -> "EPSG:4978"
 * - "LOCAL" -> "LOCAL" (unchanged)
 */
export function normalizeCrsCode(input: string): string {
  const code = input.trim();
  
  // Handle URN format
  if (code.startsWith('urn:ogc:def:crs:EPSG::')) {
    const numeric = code.split('::').pop();
    return `EPSG:${numeric}`;
  }
  
  // Handle EPSG: prefix (case insensitive)
  if (code.toUpperCase().startsWith('EPSG:')) {
    const numeric = code.slice(5);
    return `EPSG:${numeric}`;
  }
  
  // Handle plain numeric
  if (/^\d+$/.test(code)) {
    return `EPSG:${code}`;
  }
  
  // Pass through other formats (like "LOCAL")
  return code;
}

/**
 * Get bundled CRS codes for optimization decisions.
 */
export function getBundledCrsCodes(): string[] {
  return Object.keys(BUNDLED_ENTRIES);
}

/**
 * Get CRS definition with bundled → IDB cache → HTTP fetch fallback.
 */
export async function getCrsDef(code: string): Promise<CrsDef> {
  const normalizedCode = normalizeCrsCode(code);
  
  // 1. Check bundled catalog first
  if (BUNDLED_ENTRIES[normalizedCode]) {
    return BUNDLED_ENTRIES[normalizedCode];
  }
  
  // 2. Check IDB cache
  const cacheKey = `crs:${normalizedCode}`;
  try {
    const cached = await get(cacheKey);
    if (cached && typeof cached === 'object') {
      return cached as CrsDef;
    }
  } catch (error) {
    // IDB access failed, continue to HTTP fetch
    console.warn(`Failed to read CRS cache for ${normalizedCode}:`, error);
  }
  
  // 3. HTTP fetch from converter backend
  const baseUrl = getConverterApiBaseUrl();
  const fetchUrl = `${baseUrl}/api/v1/crs/${encodeURIComponent(normalizedCode)}`;
  
  try {
    const response = await fetch(fetchUrl);
    
    if (response.status === 404) {
      throw new UnknownCrsError(normalizedCode, `CRS not found: ${normalizedCode}`);
    }
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const crsDef = await response.json() as CrsDef;
    
    // Cache successful result in IDB
    try {
      await set(cacheKey, crsDef);
    } catch (error) {
      // Cache write failed, but we have the data
      console.warn(`Failed to cache CRS definition for ${normalizedCode}:`, error);
    }
    
    return crsDef;
    
  } catch (error) {
    if (error instanceof UnknownCrsError) {
      throw error;
    }
    
    // Network or other error
    throw new UnknownCrsError(
      normalizedCode,
      `Failed to fetch CRS definition: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error
    );
  }
}

/**
 * Preload multiple CRS definitions for batch operations.
 * Fetches only those not already in bundled catalog or IDB cache.
 */
export async function preloadCrsDefs(codes: string[]): Promise<void> {
  const normalizedCodes = codes.map(normalizeCrsCode);
  const toFetch: string[] = [];
  
  // Filter out codes that are already bundled or cached
  for (const code of normalizedCodes) {
    if (BUNDLED_ENTRIES[code]) {
      continue; // Already bundled
    }
    
    // Check cache
    try {
      const cached = await get(`crs:${code}`);
      if (cached) {
        continue; // Already cached
      }
    } catch {
      // Cache read failed, assume not cached
    }
    
    toFetch.push(code);
  }
  
  if (toFetch.length === 0) {
    return; // Nothing to fetch
  }
  
  // Fetch all missing definitions in parallel
  const baseUrl = getConverterApiBaseUrl();
  const fetchPromises = toFetch.map(async (code) => {
    const fetchUrl = `${baseUrl}/api/v1/crs/${encodeURIComponent(code)}`;
    
    try {
      const response = await fetch(fetchUrl);
      
      if (response.ok) {
        const crsDef = await response.json() as CrsDef;
        // Cache the result
        try {
          await set(`crs:${code}`, crsDef);
        } catch (error) {
          console.warn(`Failed to cache preloaded CRS ${code}:`, error);
        }
      }
      // Ignore 404s and other errors during preload
    } catch (error) {
      // Ignore network errors during preload
      console.warn(`Failed to preload CRS ${code}:`, error);
    }
  });
  
  await Promise.allSettled(fetchPromises);
}

// ---------------------------------------------------------------------------
// CRS search — queries the converter backend
// ---------------------------------------------------------------------------

export interface CrsSearchResult {
  epsgCode: string;
  name: string;
  kind: CrsDef['kind'];
}

/**
 * Search the CRS catalogue via `GET /api/v1/crs/search?q={query}`.
 *
 * Returns an empty array on network errors so callers can degrade
 * gracefully (e.g. show "no results" instead of crashing).
 */
export async function searchCrs(query: string): Promise<CrsSearchResult[]> {
  if (!query.trim()) return [];

  const baseUrl = getConverterApiBaseUrl();
  const url = `${baseUrl}/api/v1/crs/search?q=${encodeURIComponent(query.trim())}`;

  try {
    const response = await fetch(url);
    if (!response.ok) return [];
    return (await response.json()) as CrsSearchResult[];
  } catch {
    return [];
  }
}